/**
 * Navigation Bar component for authenticated users
 * Enhanced with collapsible search and fixed positioning
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { 
  Home, 
  User, 
  MessageSquare, 
  UserPlus,
  LogOut, 
  Menu, 
  X, 
  Bell, 
  Sun, 
  Moon,
  Search,
  Book,
  GraduationCap,
  Users,
  Briefcase,
  FileText,
  Check,
  Clock,
  AlertTriangle, 
  Loader,
  Building,
  Mic
} from 'lucide-react';
import { logout } from '../../services/firebase/authService';
import { 
  subscribeToNotifications, 
  markAllNotificationsAsRead, 
  markNotificationAsRead, 
  formatNotificationTime,
  getUnreadGeneralNotificationCount 
} from '../../services/firebase/notificationService';
import useConnections from '../../features/connect/hooks/useConnections';
import useUnreadMessages from '../../features/messaging/hooks/useUnreadMessages';
import useConnectionNotifications from '../../features/connect/hooks/useConnectionNotifications';

/**
 * NavBar component for authenticated users with collapsible search and fixed positioning
 * 
 * @returns {JSX.Element} NavBar component
 */
const NavBar = () => {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [viewMode, setViewMode] = useState('social'); // 'social' or 'career'
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false); // New state for search visibility
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [generalUnreadCount, setGeneralUnreadCount] = useState(0); // For bell icon
  const searchInputRef = useRef(null); // Ref for search input
  
  // Get unread message count
  const unreadMessageCount = useUnreadMessages();
  
  // Get connection notifications (for Connect badge)
  const { unreadConnectionCount } = useConnectionNotifications();
  
  // Action status tracking
  const [actionStatuses, setActionStatuses] = useState({});
  
  // Use the connections hook
  const { 
    acceptRequest, 
    declineRequest, 
    refreshConnections, 
    refreshPendingRequests 
  } = useConnections();

  // Determine current view mode based on route
  useEffect(() => {
    if (location.pathname.includes('/career')) {
      setViewMode('career');
    } else {
      setViewMode('social');
    }
  }, [location.pathname]);

  // Subscribe to all notifications (for bell icon - excluding connection notifications)
  useEffect(() => {
    if (!currentUser) return;
    
    const unsubscribe = subscribeToNotifications(currentUser.uid, (notifications) => {
      // Filter out notifications that are complete (handled)
      const filteredNotifications = notifications.filter(n => !actionStatuses[n.id]?.complete);
      setNotifications(filteredNotifications);
      
      // Count unread general notifications (excluding connection notifications)
      const unreadGeneralNotifications = filteredNotifications.filter(n => 
        !n.isRead && 
        n.type !== 'connection_request' && 
        n.type !== 'connection_accepted'
      );
      setGeneralUnreadCount(unreadGeneralNotifications.length);
    });
    
    return () => unsubscribe();
  }, [currentUser, actionStatuses]);

  // Focus search input when search is shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearch && !event.target.closest('.search-container') && 
          !event.target.closest('.search-trigger')) {
        setShowSearch(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-panel') && 
          !event.target.closest('.notifications-trigger')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Toggle search visibility
  const toggleSearch = () => {
    setShowSearch(!showSearch);
    if (!showSearch) {
      setSearchQuery('');
    }
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      if (viewMode === 'career') {
        navigate(`/career-opportunities?search=${encodeURIComponent(searchQuery)}`);
      } else {
        console.log(`Searching for: ${searchQuery}`);
      }
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  // Switch between social and career views
  const switchViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'social' && location.pathname.includes('/career')) {
      navigate('/home');
    } else if (mode === 'career' && !location.pathname.includes('/career')) {
      navigate('/career-opportunities');
    }
  };
  
  // Handle marking all notifications as read
  const handleMarkAllAsRead = async () => {
    if (!currentUser) return;
    try {
      await markAllNotificationsAsRead(currentUser.uid);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };
  
  // Handle marking a single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  // Handle accepting a connection request
  const handleAcceptRequest = async (requestId, notificationId) => {
    try {
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'processing', action: 'accept' }
      }));
      
      await acceptRequest(requestId);
      await markNotificationAsRead(notificationId);
      await refreshConnections();
      await refreshPendingRequests();
      
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'success', action: 'accept', message: 'Connection accepted!' }
      }));
      
      setTimeout(() => {
        setActionStatuses(prev => ({
          ...prev,
          [notificationId]: { ...prev[notificationId], complete: true }
        }));
      }, 2000);
      
    } catch (error) {
      console.error('Error accepting request:', error);
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'error', action: 'accept', message: 'Failed to accept' }
      }));
      
      setTimeout(() => {
        setActionStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[notificationId];
          return newStatuses;
        });
      }, 3000);
    }
  };
  
  // Handle declining a connection request
  const handleDeclineRequest = async (requestId, notificationId) => {
    try {
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'processing', action: 'decline' }
      }));
      
      await declineRequest(requestId);
      await markNotificationAsRead(notificationId);
      await refreshPendingRequests();
      
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'success', action: 'decline', message: 'Request declined' }
      }));
      
      setTimeout(() => {
        setActionStatuses(prev => ({
          ...prev,
          [notificationId]: { ...prev[notificationId], complete: true }
        }));
      }, 2000);
      
    } catch (error) {
      console.error('Error declining request:', error);
      setActionStatuses(prev => ({
        ...prev,
        [notificationId]: { status: 'error', action: 'decline', message: 'Failed to decline' }
      }));
      
      setTimeout(() => {
        setActionStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[notificationId];
          return newStatuses;
        });
      }, 3000);
    }
  };
  
  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (notification.isRead) return;
    
    handleMarkAsRead(notification.id);
    
    if (notification.type === 'connection_request') {
      navigate('/connect?tab=requests');
    } else if (notification.type === 'message') {
      navigate(`/messages?user=${notification.content.senderId}`);
    } else if (notification.type === 'assignment') {
      navigate('/assignments');
    } else if (notification.type === 'job_application') {
      navigate('/career/applications');
    } else if (notification.type === 'resource_review') {
      navigate('/resources');
    }
    
    setShowNotifications(false);
  };

  // Navigation links for social view
  const socialNavLinks = [
    { name: 'Home', path: '/home', icon: <Home size={20} /> },
    { 
      name: 'Connect', 
      path: '/connect', 
      icon: <UserPlus size={20} />,
      badge: unreadConnectionCount // Now only shows connection notifications
    },
    { 
      name: 'Messages', 
      path: '/messages', 
      icon: <MessageSquare size={20} />,
      badge: unreadMessageCount
    },
    { name: 'Profile', path: '/profile', icon: <User size={20} /> },
  ];

  // Navigation links for career view
  const careerNavLinks = [
    { name: 'Jobs', path: '/career-opportunities', icon: <Briefcase size={20} /> },
    { name: 'Applications', path: '/career/applications', icon: <FileText size={20} /> },
    { name: 'CV Manager', path: '/career/cv-manager', icon: <User size={20} /> },
    { name: 'Screening', path: '/career/screening', icon: <Mic size={20} /> },
  ];

  // Get active navigation links based on view mode
  const navLinks = viewMode === 'social' ? socialNavLinks : careerNavLinks;

  // Check if link is active
  const isActive = (path) => location.pathname === path;

  // Render connection request notification with action status
  const renderConnectionRequestNotification = (notification) => {
    const actionStatus = actionStatuses[notification.id];
    
    if (actionStatus?.complete) return null;
    
    if (actionStatus) {
      return (
        <div key={notification.id} className={`p-3 border-b border-gray-100 ${
          actionStatus.status === 'success' ? 'bg-green-100' :
          actionStatus.status === 'error' ? 'bg-red-100' :
          'bg-gray-100'
        }`}>
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full flex-shrink-0 mr-3 flex items-center justify-center">
              {actionStatus.status === 'processing' ? (
                <Loader className="h-6 w-6 text-gray-500 animate-spin" />
              ) : actionStatus.status === 'success' ? (
                <Check className="h-6 w-6 text-green-500" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-red-500" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium">
                {actionStatus.message || (actionStatus.status === 'processing' ? 'Processing...' : 'Done')}
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div key={notification.id} className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
        <div className="flex items-start">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mr-3">
            {notification.content.senderPhoto ? (
              <img 
                src={notification.content.senderPhoto} 
                alt={notification.content.senderName} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                {notification.content.senderName.charAt(0)}
              </div>
            )}
          </div>
          <div>
<p className="text-sm text-gray-800">
              <span className="font-medium">{notification.content.senderName}</span> sent you a connection request
              {notification.content.message && (
                <span className="block mt-1 text-xs italic">"{notification.content.message}"</span>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
          </div>
        </div>
      </div>
    );
  };

  // Render notification item based on type
  const renderNotificationItem = (notification) => {
    switch (notification.type) {
      case 'connection_request':
        return renderConnectionRequestNotification(notification);
        
      case 'connection_accepted':
        return (
          <div 
            key={notification.id} 
            className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mr-3">
                {notification.content.acceptorPhoto ? (
                  <img 
                    src={notification.content.acceptorPhoto} 
                    alt={notification.content.acceptorName} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                    {notification.content.acceptorName.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-800">
                  <span className="font-medium">{notification.content.acceptorName}</span> accepted your connection request
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
              </div>
            </div>
          </div>
        );
        
      case 'message':
        return (
          <div 
            key={notification.id} 
            className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 mr-3">
                {notification.content.senderPhoto ? (
                  <img 
                    src={notification.content.senderPhoto} 
                    alt={notification.content.senderName} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                    {notification.content.senderName.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-800">
                  <span className="font-medium">{notification.content.senderName}</span> sent you a message
                </p>
                <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                  {notification.content.message}
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
              </div>
            </div>
          </div>
        );

      case 'job_application':
        return (
          <div 
            key={notification.id} 
            className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mr-3">
                <Briefcase className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-800">
                  Application status update for <span className="font-medium">{notification.content.jobTitle}</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  Status: <span className={`font-medium ${
                    notification.content.status === 'accepted' ? 'text-green-600' :
                    notification.content.status === 'rejected' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {notification.content.status.charAt(0).toUpperCase() + notification.content.status.slice(1)}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
              </div>
            </div>
          </div>
        );

      case 'resource_review':
        return (
          <div 
            key={notification.id} 
            className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'} cursor-pointer`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mr-3">
                {notification.content.status === 'approved' ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <FileText className="w-5 h-5 text-yellow-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {notification.content.title}
                </p>
                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                  {notification.content.message}
                </p>
                <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div 
            key={notification.id} 
            className={`p-3 border-b border-gray-100 ${!notification.isRead ? 'bg-green-50' : 'hover:bg-gray-50'}`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mr-3">
                <Bell className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-800">
                  {notification.content.title || 'Notification'}
                </p>
                {notification.content.message && (
                  <p className="text-xs text-gray-600 mt-1">{notification.content.message}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">{formatNotificationTime(notification.createdAt)}</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {/* Spacer - Reduced height since we removed smart scroll */}
      <div className="h-[106px] md:h-[74px]"></div>

      {/* Fixed NavBar */}
      <div className="fixed top-0 left-0 right-0 z-50 shadow-md">
        {/* Mode Switcher */}
        <div className="bg-gray-100 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-10">
              <div className="flex space-x-4">
                <button
                  onClick={() => switchViewMode('social')}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    viewMode === 'social'
                      ? 'bg-green-500 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Social
                </button>
                <button
                  onClick={() => switchViewMode('career')}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    viewMode === 'career'
                      ? 'bg-green-500 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Career
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {viewMode === 'social' 
                  ? 'Connect with fellow students' 
                  : 'Explore career opportunities'}
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                <Link to="/home" className="flex items-center space-x-2">
                  <div className="relative">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                      <span className="text-white font-bold text-lg transform -rotate-12">V</span>
                    </div>
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                  </div>
                  <span className="text-lg font-bold text-gray-800">
                    <span className="text-green-500">Varsity</span>Link
                  </span>
                </Link>
              </div>

              {/* Search Bar (Desktop) - Now Collapsible */}
              <div className="hidden md:block flex-1 max-w-md mx-4">
                {showSearch ? (
                  <form onSubmit={handleSearchSubmit} className="relative search-container">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={viewMode === 'social' ? "Search users, posts..." : "Search jobs, companies..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search size={18} className="text-gray-400" />
                    </div>
                    <button
                      type="button"
                      onClick={toggleSearch}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <X size={18} />
                    </button>
                  </form>
                ) : (
                  <div className="flex justify-center">
                    <button
                      onClick={toggleSearch}
                      className="search-trigger p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                      aria-label="Open search"
                    >
                      <Search size={20} />
                    </button>
                  </div>
                )}
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`flex items-center space-x-1 transition-colors relative ${
                      isActive(link.path) 
                        ? 'text-green-500 font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {link.icon}
                    <span>{link.name}</span>
                    {link.badge > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {link.badge > 9 ? '9+' : link.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>

              {/* User Menu (Desktop) */}
              <div className="hidden md:flex items-center space-x-4">
                <button 
                  className="text-gray-600 hover:text-gray-900 focus:outline-none relative notifications-trigger"
                  onClick={() => setShowNotifications(!showNotifications)}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                  {generalUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {generalUnreadCount}
                    </span>
                  )}
                </button>
                
                <button 
                  onClick={toggleDarkMode}
                  className="text-gray-600 hover:text-gray-900 focus:outline-none"
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>

                <div className="flex items-center space-x-2">
                  <Link to="/profile" className="relative">
                    <div className="w-8 h-8 bg-gray-200 rounded-full overflow-hidden">
                      {userProfile?.photos && userProfile.photos.length > 0 ? (
                        <img 
                          src={userProfile.photos[0]} 
                          alt={userProfile?.basicInfo?.name || 'User'} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                          {userProfile?.basicInfo?.name?.charAt(0) || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-red-500 focus:outline-none"
                    aria-label="Logout"
                  >
                    <LogOut size={20} />
                  </button>
                </div>
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center space-x-4">
                <button
                  onClick={toggleSearch}
                  className="search-trigger text-gray-600 hover:text-gray-900 focus:outline-none"
                  aria-label="Search"
                >
                  <Search size={20} />
                </button>
                
                <button 
                  className="text-gray-600 hover:text-gray-900 focus:outline-none relative notifications-trigger"
                  onClick={() => setShowNotifications(!showNotifications)}
                  aria-label="Notifications"
                >
                  <Bell size={20} />
                  {generalUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {generalUnreadCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-gray-600 hover:text-gray-900 focus:outline-none"
                  aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                >
                  {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
            </div>

            {/* Mobile Search Bar - Collapsible */}
            {showSearch && (
              <div className="md:hidden pb-3 search-container">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={viewMode === 'social' ? "Search users, posts..." : "Search jobs, companies..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={18} className="text-gray-400" />
                  </div>
                  <button
                    type="button"
                    onClick={toggleSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                </form>
              </div>
            )}

            {/* Mobile Navigation */}
            {isMenuOpen && (
              <div className="md:hidden py-4 border-t border-gray-100 bg-white max-h-[calc(100vh-106px)] overflow-y-auto">
                <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-gray-100">
                  <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden">
                    {userProfile?.photos && userProfile.photos.length > 0 ? (
                      <img 
                        src={userProfile.photos[0]} 
                        alt={userProfile?.basicInfo?.name || 'User'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-green-100 text-green-500">
                        {userProfile?.basicInfo?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">
                      {userProfile?.basicInfo?.name || 'User'}
                    </div>
                    <div className="text-sm text-gray-500">
                      @{userProfile?.basicInfo?.username || 'username'}
                    </div>
                  </div>
                </div>
              
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`flex items-center space-x-3 py-3 px-2 rounded-lg ${
                      isActive(link.path) 
                        ? 'bg-green-50 text-green-500 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.icon}
                    <span>{link.name}</span>
                    {link.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ml-auto">
                        {link.badge > 9 ? '9+' : link.badge}
                      </span>
                    )}
                  </Link>
                ))}
                
                <div className="border-t border-gray-100 mt-4 pt-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">Switch View</div>
                  <div className="flex space-x-2 mb-4">
                    <button
                      onClick={() => {
                        switchViewMode('social');
                        setIsMenuOpen(false);
                      }}
                      className={`flex-1 py-2 rounded-md ${
                        viewMode === 'social'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      Social
                    </button>
                    <button
                      onClick={() => {
                        switchViewMode('career');
                        setIsMenuOpen(false);
                      }}
                      className={`flex-1 py-2 rounded-md ${
                        viewMode === 'career'
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      Career
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <button 
                    onClick={toggleDarkMode}
                    className="flex items-center space-x-3 py-3 px-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  >
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                    <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 py-3 px-2 rounded-lg text-red-500 hover:bg-red-50"
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>
      
        {/* Notifications Popover */}
        {showNotifications && (
          <div className="notifications-panel absolute right-4 mt-2 w-80 bg-white rounded-lg shadow-lg z-50 overflow-hidden border border-gray-200 md:right-auto md:left-1/2 md:transform md:-translate-x-40">
            <div className="p-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-medium">Notifications</h3>
              {generalUnreadCount > 0 && (
                <button 
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-green-500 hover:text-green-600"
                >
                  Mark all as read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.length > 0 ? (
                notifications.map(notification => {
                  if (notification.type === 'connection_request' && !notification.isRead) {
                    return renderConnectionRequestNotification(notification);
                  }
                  return renderNotificationItem(notification);
                })
              ) : (
                <div className="p-6 text-center">
                  <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Bell className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">No notifications yet</p>
                </div>
              )}
              {notifications.length > 0 && (
                <div className="p-3 text-center text-sm text-gray-500 border-t border-gray-100">
                  No more notifications
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NavBar;